import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
    Dialog, DialogContent, DialogActions, Button, TextField, Box, Typography,
    CircularProgress, InputAdornment, IconButton, Alert, LinearProgress, Tooltip,
} from '@mui/material';
import {
    Visibility, VisibilityOff,
    LockReset as LockResetIcon,
    LockOutlined as LockOutlinedIcon,
    Person as PersonIcon,
    Lock as LockIcon,
    CheckCircle as CheckIcon,
    Cancel as CrossIcon,
    ShieldOutlined as ShieldIcon,
} from '@mui/icons-material';

import { BRAND, PRODUCT, FONT_STACK, brandFieldSx, brandButtonSx, GRADIENT_NAVY } from '../brand';
import { first_password, changestaffpassword } from './BaseURL';

// ============================================================================
//  FirstLoginDialog — mandatory password change on first sign-in
// ============================================================================
//
//  Shown the instant a user with first_login = 1 authenticates, before they
//  reach any part of the application. Two things it makes visually obvious,
//  because both are enforced server-side and neither is negotiable:
//
//    • The USERNAME IS FIXED. It is displayed read-only, with a lock icon and
//      an explicit note. There is no input for it, no "edit" affordance, and
//      no endpoint behind this dialog that could change it.
//    • The PASSWORD MUST CHANGE. The dialog cannot be dismissed by clicking
//      away or pressing Escape — the only exits are completing the change or
//      signing out.
//
//  Props
//    open, username, email, trans_code   the authenticated (but not yet
//                                        admitted) user
//    currentPassword                     what they just signed in with; sent
//                                        so the server can re-verify identity
//    onCancel                            abandon and return to the login form
//    onSuccess(username)                 password changed — sign in again

// Mirrors validatePassword() in backend/setup_routes.js. THIS is where the
// policy actually bites — the setup wizard deliberately accepts anything,
// because that password is a handover credential replaced right here.
const RULES = [
    { key: 'len',   label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { key: 'lower', label: 'A lowercase letter',    test: (p) => /[a-z]/.test(p) },
    { key: 'upper', label: 'An uppercase letter',   test: (p) => /[A-Z]/.test(p) },
    { key: 'digit', label: 'A number',              test: (p) => /[0-9]/.test(p) },
    { key: 'sym',   label: 'A symbol (! @ # $)',    test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function score(password, username) {
    if (!password) return { pct: 0, label: '', color: BRAND.line };
    // All five rules are the bar; genuine length is the only bonus left.
    let passed = RULES.filter((r) => r.test(password)).length;
    if (password.length >= 12) passed += 1;
    if (username && password.toLowerCase().includes(username.toLowerCase())) passed -= 2;

    const pct = Math.max(0, Math.min(100, (passed / 6) * 100));
    if (pct < 40)  return { pct, label: 'Weak',        color: BRAND.danger };
    if (pct < 70)  return { pct, label: 'Fair',        color: BRAND.warning };
    if (pct < 100) return { pct, label: 'Strong',      color: BRAND.green };
    return             { pct, label: 'Very strong',    color: BRAND.greenDark };
}

function FirstLoginDialog({
    open, username, email, trans_code, currentPassword,
    onCancel, onClose, onSuccess,
}) {
    const [newPassword, setNewPassword]         = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNew, setShowNew]                 = useState(false);
    const [showConfirm, setShowConfirm]         = useState(false);
    const [error, setError]                     = useState('');
    const [loading, setLoading]                 = useState(false);

    const strength = useMemo(() => score(newPassword, username), [newPassword, username]);
    const allRulesPass = RULES.every((r) => r.test(newPassword));
    const canSubmit = allRulesPass && newPassword === confirmPassword && !loading;

    const reset = () => {
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        setShowNew(false);
        setShowConfirm(false);
    };

    const handleSubmit = async () => {
        setError('');

        // Local pre-checks so the common mistakes never cost a round trip.
        const failed = RULES.find((r) => !r.test(newPassword));
        if (failed) { setError(`Your password needs: ${failed.label.toLowerCase()}.`); return; }
        if (username && newPassword.toLowerCase().includes(String(username).toLowerCase())) {
            setError('Your password must not contain your username.');
            return;
        }
        if (newPassword !== confirmPassword) { setError('The two passwords do not match.'); return; }

        setLoading(true);
        try {
            const { data } = await axios.post(first_password, {
                trans_code,
                currentPassword,
                newPassword,
            });

            if (data.success) {
                reset();
                onSuccess?.(data.username || username);
            } else {
                setError(data.message || 'Could not update your password.');
            }
        } catch (err) {
            // Fall back to the legacy endpoint if this server has not yet been
            // updated with setup_routes.js — an older backend still supports
            // the original change-password path.
            if (err.response?.status === 404) {
                try {
                    const { data } = await axios.post(changestaffpassword, {
                        email,
                        trans_code,
                        new_password: newPassword,
                    });
                    if (data.success) {
                        reset();
                        onSuccess?.(username);
                        return;
                    }
                    setError(data.message || 'Could not update your password.');
                } catch (legacyErr) {
                    setError(legacyErr.response?.data?.message || 'Could not update your password.');
                }
            } else {
                setError(err.response?.data?.message || 'Could not update your password. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        reset();
        (onCancel || onClose)?.();
    };

    return (
        <Dialog
            open={open}
            // Mandatory: no backdrop-click, no Escape. The user leaves by
            // finishing or by explicitly signing out.
            disableEscapeKeyDown
            onClose={(e, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                handleCancel();
            }}
            maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden', fontFamily: FONT_STACK } }}
        >
            {/* ── Header ── */}
            <Box sx={{ background: GRADIENT_NAVY, px: 3.5, py: 3, color: '#fff', position: 'relative', overflow: 'hidden' }}>
                <Box aria-hidden sx={{ position: 'absolute', top: -60, right: -50, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${BRAND.green}44 0%, transparent 65%)` }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative' }}>
                    <Box sx={{ width: 46, height: 46, borderRadius: '12px', background: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.22)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                        <LockResetIcon sx={{ fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '1.4px', textTransform: 'uppercase', color: 'rgba(255,255,255,.6)' }}>
                            {PRODUCT.vendor} · Security
                        </Typography>
                        <Typography sx={{ fontSize: 19, fontWeight: 800, letterSpacing: '-.3px', lineHeight: 1.25 }}>
                            Set your own password
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <DialogContent sx={{ px: 3.5, py: 3.5, background: '#fff' }}>
                <Typography sx={{ fontSize: 13.5, color: BRAND.inkMuted, lineHeight: 1.75, mb: 3 }}>
                    This is your first sign-in. The password you used was issued during setup and
                    is known to whoever installed the system — choose a new one that only you know.
                    You will not be able to continue until you do.
                </Typography>

                {/* ── The fixed username ── */}
                <Box
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 2, mb: 3,
                        px: 2.25, py: 1.75, borderRadius: '12px',
                        background: BRAND.navySoft, border: `1px solid ${BRAND.navy}1F`,
                    }}
                >
                    <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0, border: `1px solid ${BRAND.navy}22` }}>
                        <PersonIcon sx={{ fontSize: 21, color: BRAND.navy }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: BRAND.inkFaint }}>
                            Your username
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 16, fontWeight: 800, color: BRAND.navy, lineHeight: 1.4,
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            }}
                            noWrap
                        >
                            {username || email || '—'}
                        </Typography>
                    </Box>
                    <Tooltip title="Your username is permanent and cannot be changed" arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .5, px: 1.25, py: .5, borderRadius: '20px', background: '#fff', border: `1px solid ${BRAND.line}`, flexShrink: 0 }}>
                            <LockOutlinedIcon sx={{ fontSize: 13, color: BRAND.inkMuted }} />
                            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: BRAND.inkMuted }}>
                                Fixed
                            </Typography>
                        </Box>
                    </Tooltip>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }}>{error}</Alert>
                )}

                <TextField
                    fullWidth autoFocus
                    label="New Password"
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                    sx={{ ...brandFieldSx, mb: 2.5 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockIcon sx={{ fontSize: 19, color: BRAND.navy }} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowNew((v) => !v)} edge="end">
                                    {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <TextField
                    fullWidth
                    label="Confirm New Password"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                    error={!!confirmPassword && confirmPassword !== newPassword}
                    helperText={
                        confirmPassword && confirmPassword !== newPassword
                            ? 'Passwords do not match'
                            : ' '
                    }
                    onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) handleSubmit(); }}
                    sx={{ ...brandFieldSx, mb: 1.5 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockIcon sx={{ fontSize: 19, color: BRAND.navy }} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowConfirm((v) => !v)} edge="end">
                                    {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {/* ── Strength + rules ── */}
                <Box sx={{ p: 2.25, borderRadius: '12px', background: BRAND.canvas, border: `1px solid ${BRAND.line}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .75 }}>
                            <ShieldIcon sx={{ fontSize: 15, color: BRAND.inkMuted }} />
                            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: BRAND.inkMuted, letterSpacing: '.4px' }}>
                                PASSWORD STRENGTH
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: strength.color }}>
                            {strength.label}
                        </Typography>
                    </Box>

                    <LinearProgress
                        variant="determinate" value={strength.pct}
                        sx={{
                            height: 6, borderRadius: 3, mb: 2, backgroundColor: BRAND.line,
                            '& .MuiLinearProgress-bar': { backgroundColor: strength.color, borderRadius: 3 },
                        }}
                    />

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                        {RULES.map((rule) => {
                            const ok = rule.test(newPassword);
                            return (
                                <Box key={rule.key} sx={{ display: 'flex', alignItems: 'center', gap: .625, minWidth: 160 }}>
                                    {ok
                                        ? <CheckIcon sx={{ fontSize: 15, color: BRAND.green }} />
                                        : <CrossIcon sx={{ fontSize: 15, color: BRAND.inkFaint }} />}
                                    <Typography sx={{ fontSize: 12.5, color: ok ? BRAND.ink : BRAND.inkMuted, fontWeight: ok ? 600 : 400 }}>
                                        {rule.label}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3.5, py: 2.5, background: '#fff', borderTop: `1px solid ${BRAND.line}`, gap: 1.5 }}>
                <Button
                    onClick={handleCancel} disabled={loading}
                    sx={{ textTransform: 'none', fontWeight: 600, color: BRAND.inkMuted }}
                >
                    Sign out
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                    variant="contained" onClick={handleSubmit} disabled={!canSubmit}
                    startIcon={loading ? <CircularProgress size={17} color="inherit" /> : <LockResetIcon />}
                    sx={{ ...brandButtonSx, px: 3.5, py: 1.1 }}
                >
                    {loading ? 'Saving…' : 'Set password'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default FirstLoginDialog;
